/*
 * Copyright (c) 2024 Amazon.com, Inc. All Rights Reserved.
 */

import "./AntiSignatureForm.css"
import {RequestType} from "../../model/RequestType";
import {IoClose} from "react-icons/io5";
import {
    AntiSignatureEntry,
    AntiSignatureFeatures,
    AntiSignatureType,
} from "../../model/AntiSignatureEntry";
import {useEffect, useState} from "react";
import {Snackbar} from "@mui/material";
import sentryFetch from "@amzn/sentry-fetch";
import {StageConfig} from "../../config/StageConfig";
import {isJSONResponse} from "../../config/Requests";
import {Region} from "../../model/Region";
/**
 * @author pdoriand Sep 04, 2024
 */
interface AntiSignatureFormProps {
    requestType: RequestType;
    setShowWindowForm: (show: boolean) => void;
    antiSignature?: AntiSignatureEntry;
    refreshAntiSignatures: () => void;
}

interface RegionResult {
    region: string;
    success: boolean;
    message: string;
}

export function AntiSignatureForm({requestType, setShowWindowForm, antiSignature, refreshAntiSignatures}: AntiSignatureFormProps) {
    const REQUEST_TYPE_TO_HTTP_METHOD: Record<string, string> = {
        [RequestType.ADD_ANTISIGNATURE]: "POST",
        [RequestType.REMOVE_ANTISIGNATURE]: "DELETE",
        [RequestType.UPDATE_ANTISIGNATURE]: "PUT"
    };

    const FORM_AUTO_CLOSE_DELAY = 3000;
    const [stageConfig, setStageConfig] = useState<StageConfig>();
    const [showMessage, setShowMessage] = useState(false);
    const [responseMessage, setResponseMessage] = useState("");
    const [region, setRegion] = useState(antiSignature?.region || Region.NA);
    const [selectedRegions, setSelectedRegions] = useState<Set<Region>>(new Set([Region.NA]));
    const [description, setDescription] = useState(antiSignature?.description || "");
    const [ttl, setTtl] = useState(antiSignature?.ttl || 0);
    const [signatureType, setSignatureType] = useState(antiSignature?.signatureType || AntiSignatureType.ANTI_HUMAN);
    const [labels, setLabels] = useState<Set<string> | undefined>(antiSignature?.labels);
    const [models, setModels] = useState<Set<string> | undefined>(antiSignature?.models);
    const [showJsonInput, setShowJsonInput] = useState(false);
    const [jsonInput, setJsonInput] = useState("");
    const [jsonValidationMessage, setJsonValidationMessage] = useState("");
    const creationTime = antiSignature?.creationTime || 0;
    // Used for giving signature features a key, so no warnings appear in the console
    let featureId = 0;

    useEffect(() => {
        fetch("../stageConfig.json")
            .then(res => res.json())
            .then(stageConfig => setStageConfig(stageConfig));
    }, []);
    const getFormTitle = (requestType: RequestType) => {
        switch (requestType) {
            case RequestType.ADD_ANTISIGNATURE:
                return "Add AntiSignature";
            case RequestType.REMOVE_ANTISIGNATURE:
                return "Remove AntiSignature";
            case RequestType.UPDATE_ANTISIGNATURE:
                return "Update AntiSignature";
        }
    }
    const addNewFeature = () => {
        const featureInput = document.createElement("select");
        featureInput.name = featureId.toString();
        AntiSignatureFeatures.forEach((value, id) => {
            const option = document.createElement("option");
            option.value = value;
            option.textContent = value;
            featureInput.appendChild(option)
        })

        const removeButton = document.createElement("button");
        removeButton.textContent = "-"
        removeButton.onclick = (e) => {
            // stopPropagation is necessary so that the window doesn't close
            e.stopPropagation();
            removeFeature(e.target!)
        };

        const featureValueInput = document.createElement("input");
        featureValueInput.type = "text";
        featureValueInput.id = featureId.toString();

        const newFeature = document.createElement("div");
        newFeature.className = "antisignature-feature-field";
        newFeature.appendChild(featureInput);
        newFeature.appendChild(featureValueInput);
        newFeature.appendChild(removeButton);
        document.getElementById("aggregatedBy")?.insertBefore(newFeature, document.getElementById("add-feature-button"));

        featureId++;
    }
    const removeFeature = (element: EventTarget) => {
        let button = element as HTMLButtonElement;
        button.parentElement?.remove();
        setShowWindowForm(true);
    }
    const getFeatureHashMap = () => {
        let signatureFeatures = new Map<string, string>();
        // Build the map of signature features
        for (const element of Array.from(document.getElementById("aggregatedBy")!.children)) {
            if (element.className !== "antisignature-feature-field") {
                continue;
            }
            let keyElement = element.children[0] as HTMLInputElement;
            let valueElement = element.children[1] as HTMLInputElement;
            if (signatureFeatures.has(keyElement.value)) {
                setResponseMessage("You can't have an AntiSignature with two identical features")
                setShowMessage(true);
                return null;
            }
            signatureFeatures.set(keyElement.value, valueElement.value);
        }

        if (signatureFeatures.size === 0) {
            setResponseMessage("Please provide signature features");
            setShowMessage(true);
            return null;
        }
        return signatureFeatures;
    }
    const validateJsonInput = () => {
        try {
            const aggregatedByMatches = jsonInput.match(/"aggregatedBy"\s*:\s*(\{[^}]*\})/g);
            
            if (!aggregatedByMatches || aggregatedByMatches.length === 0) {
                setJsonValidationMessage("Invalid input: No aggregatedBy field found");
                return;
            }
            
            if (aggregatedByMatches.length > 1) {
                setJsonValidationMessage("Invalid input: Multiple aggregatedBy fields found");
                return;
            }
            
            const aggregatedByMatch = aggregatedByMatches[0].match(/"aggregatedBy"\s*:\s*(\{[^}]*\})/);
            if (aggregatedByMatch) {
                const aggregatedByJson = aggregatedByMatch[1];
                const keyMatches = aggregatedByJson.match(/"([^"]+)"\s*:/g);
                if (keyMatches) {
                    const keys = keyMatches.map(match => match.replace(/"([^"]+)"\s*:/, '$1'));
                    const uniqueKeys = new Set(keys);
                    if (keys.length !== uniqueKeys.size) {
                        setJsonValidationMessage("Invalid input: Duplicate keys found in aggregatedBy");
                        return;
                    }
                }
                
                const aggregatedByObject = JSON.parse(aggregatedByMatch[1]);
                if (Object.keys(aggregatedByObject).length === 0) {
                    setJsonValidationMessage("Invalid input: aggregatedBy object is empty");
                    return;
                }

                const invalidKeys = Object.keys(aggregatedByObject).filter(key => !AntiSignatureFeatures.includes(key));
                if (invalidKeys.length > 0) {
                    setJsonValidationMessage(`Invalid input: Unknown keys found: ${invalidKeys.join(", ")}`);
                    return;
                }

                const existingFields = document.querySelectorAll(".antisignature-feature-field");
                existingFields.forEach(field => field.remove());

                Object.entries(aggregatedByObject).forEach(([key, value]) => {
                    const featureInput = document.createElement("select");
                    featureInput.name = featureId.toString();
                    featureInput.value = key;
                    AntiSignatureFeatures.forEach((featureValue) => {
                        const option = document.createElement("option");
                        option.value = featureValue;
                        option.textContent = featureValue;
                        if (featureValue === key) option.selected = true;
                        featureInput.appendChild(option);
                    });
                    
                    const featureValueInput = document.createElement("input");
                    featureValueInput.type = "text";
                    featureValueInput.id = featureId.toString();
                    featureValueInput.value = value as string;
                    
                    const removeButton = document.createElement("button");
                    removeButton.textContent = "-";
                    removeButton.onclick = (e) => {
                        e.stopPropagation();
                        removeFeature(e.target!);
                    };
                    
                    const newFeature = document.createElement("div");
                    newFeature.className = "antisignature-feature-field";
                    newFeature.appendChild(featureInput);
                    newFeature.appendChild(featureValueInput);
                    newFeature.appendChild(removeButton);
                    document.getElementById("aggregatedBy")?.insertBefore(newFeature, document.getElementById("add-feature-button"));
                    
                    featureId++;
                });
                
                setJsonValidationMessage("Valid input: aggregatedBy extracted successfully");
                setShowJsonInput(false);
            }
        } catch (error) {
            setJsonValidationMessage("Invalid input: JSON parsing error");
        }
    };

    const sendAntiSignatureRequest = async (selectedRegion: Region, signatureFeatures: Map<string, string>, antiSignatureModelsArray: string[], antiSignatureLabelsArray: string[]): Promise<RegionResult> => {
        const requestBody = {
            requestType,
            region: selectedRegion,
            description: description || "",
            ttl: ttl || 0,
            creationTime: creationTime || Date.now(),
            models: antiSignatureModelsArray,
            labels: antiSignatureLabelsArray,
            signatureType: signatureType || "",
            aggregatedBy: Object.fromEntries(signatureFeatures),
            author: antiSignature?.author
        };

        try {
            const response = await sentryFetch(`${stageConfig!.url}/modelsManager`, {
                method: "POST",
                credentials: "include",
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody),
                sentryOptions: {
                    followMidwayStepUp: true
                }
            });

            if (response.ok) {
                const responseText = await response.text();
                const message = isJSONResponse(responseText) ? JSON.parse(responseText).message : responseText;
                return { region: selectedRegion, success: true, message };
            } else {
                const errorText = await response.text();
                const errorMessage = isJSONResponse(errorText) ? JSON.parse(errorText).message : errorText;
                return { region: selectedRegion, success: false, message: errorMessage };
            }
        } catch (error: any) {
            return { region: selectedRegion, success: false, message: error.message };
        }
    };

    const getLabels = async () => {
        if (!stageConfig) return;

        const unknownLabels: string[] = [];

        const antiSignatureLabels = Array.from(labels || []).map(value => value.trim());

        if (requestType === RequestType.REMOVE_ANTISIGNATURE) {
            return antiSignatureLabels.join(",");
        }

        for (const label of antiSignatureLabels) {
            const response = await sentryFetch(`${stageConfig.url}/modelsManager?requestType=${RequestType.SEARCH_MODELS}&label=${label}&region=&detectionSystem=&publishable=&ttlGreaterThan=&ttlLessThan=&ttlEquals=&lastCommitTimestampFrom=&lastCommitTimestampTo=&modelName=&lastCommitDescription=&lastCommitAuthor=&page=1&valuesPerPage=1`);
            if (response.ok) {
                const models = await response.json();
                if (models.length === 0) {
                    unknownLabels.push(label);
                }
            } else {
                setResponseMessage("The labels could not be verified")
                setShowMessage(true);
                return null;
            }
        }
        if (unknownLabels.length !== 0) {
            if (!window.confirm("The following labels do not appear on any known modelhub model: " + unknownLabels.join(" , ") + "\nAre you sure you want to add the AntiSignature with these labels?")) {
                setResponseMessage("AntiSignature has not been saved");
                setShowMessage(true);
                return null;
            }
        }
        return antiSignatureLabels.join(",");
    }

    const submitRequest = async () => {
        if (!stageConfig) return;

        if (description === "") {
            setResponseMessage("Please provide a valid description");
            setShowMessage(true);
            return;
        }

        const signatureFeatures = getFeatureHashMap()
        if (signatureFeatures === null || signatureFeatures === undefined) {
            return;
        }

        const antiSignatureLabels = await getLabels();
        if (antiSignatureLabels === null || antiSignatureLabels === undefined) {
            return;
        }

        const antiSignatureModelsArray = Array.from(models || []).map(model => model.trim()).filter(model => model.length > 0);
        const antiSignatureLabelsArray = Array.from(labels || []).map(label => label.trim()).filter(label => label.length > 0);

        if (requestType === RequestType.ADD_ANTISIGNATURE) {
            if (selectedRegions.size === 0) {
                setResponseMessage("Please select at least one region");
                setShowMessage(true);
                return;
            }

            const results: RegionResult[] = [];
            
            for (const selectedRegion of Array.from(selectedRegions)) {
                const result = await sendAntiSignatureRequest(selectedRegion, signatureFeatures, antiSignatureModelsArray, antiSignatureLabelsArray);
                results.push(result);
            }

            const successCount = results.filter(r => r.success).length;
            const failureCount = results.filter(r => !r.success).length;
            
            let summaryMessage = `${successCount} antisignature(s) added successfully`;
            if (failureCount > 0) {
                summaryMessage += `, ${failureCount} failed`;
                const failures = results.filter(r => !r.success);
                const failureDetails = failures.map(f => `${f.region}: ${f.message}`).join('; ');
                summaryMessage += ` (${failureDetails})`;
            }
            
            setResponseMessage(summaryMessage);
            setShowMessage(true);
            refreshAntiSignatures();

            setTimeout(() => {
                setShowWindowForm(false);
            }, FORM_AUTO_CLOSE_DELAY);
            return;
        }

        let requestUrl: string;
        let requestBody: any;
        
        if (REQUEST_TYPE_TO_HTTP_METHOD[requestType]) {
            requestUrl = `${stageConfig.url}/modelsManager`;
            requestBody = {
                requestType,
                region: region || "",
                description: description || "",
                ttl: ttl || 0,
                creationTime: creationTime || Date.now(),
                models: antiSignatureModelsArray,
                labels: antiSignatureLabelsArray,
                signatureType: signatureType || "",
                aggregatedBy: Object.fromEntries(signatureFeatures),
                author: antiSignature?.author
            };
        } else {
            const antiSignatureModels = Array.from(models || []).map(model => model.trim()).join(",");
            requestUrl = `${stageConfig.url}/modelsManager?requestType=${requestType}` +
                `&region=${region || ""}&description=${description || ""}&ttl=${ttl || 0}&creationTime=${creationTime || Date.now()}` +
                `&models=[${antiSignatureModels}]&labels=[${antiSignatureLabels}]&signatureType=${signatureType || ""}` +
                `&aggregatedBy=${encodeURIComponent(JSON.stringify(Object.fromEntries(signatureFeatures)))}`;
            requestBody = undefined;
        }

        const getMethod = () => {
            return REQUEST_TYPE_TO_HTTP_METHOD[requestType] || "GET";
        };

        sentryFetch(requestUrl, {
            method: getMethod(),
            credentials: "include",
            ...(REQUEST_TYPE_TO_HTTP_METHOD[requestType] && {
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            }),
            sentryOptions: {
                followMidwayStepUp: true
            }
        })
            .then(response => {
                if (response.ok) {
                    return response.text().then(response => {
                        if (isJSONResponse(response)) {
                            return JSON.parse(response).message;
                        } else {
                            return response;
                        }
                    });
                } else {
                    return response.text().then(response => {
                        if(isJSONResponse(response)) {
                            throw new Error(JSON.parse(response).message);
                        } else {
                            throw new Error(response);
                        }
                    })
                }
            })
            .then(response => {
                switch (requestType) {
                    case RequestType.UPDATE_ANTISIGNATURE:
                        setResponseMessage("AntiSignature has been updated");
                        break;
                    case RequestType.REMOVE_ANTISIGNATURE:
                        setResponseMessage("AntiSignature has been removed");
                        break;
                    default:
                        setResponseMessage("Unknown request");
                }
                setShowMessage(true);
                refreshAntiSignatures();

                setTimeout(() => {
                    setShowWindowForm(false);
                }, 1000);
            })
            .catch(error => {
                console.error(error.message);
                setResponseMessage(error.message);
                setShowMessage(true);
            })
    }
    return <>
        <div className="antisignature-form-header">
            <div/>
            <p className="text-center">{getFormTitle(requestType)}</p>
            <IoClose className="x-button" onClick={() => setShowWindowForm(false)}/>
        </div>
        <div className="aggregated-by-container">
            <p>AggregatedBy</p>
            <div id="aggregatedBy">
                {antiSignature && antiSignature.aggregatedBy ? Object.entries(antiSignature.aggregatedBy).map((entry) => {
                        featureId++;
                        return <div className="antisignature-feature-field" key={featureId} id={featureId.toString()}>
                            <select disabled={requestType !== RequestType.ADD_ANTISIGNATURE} defaultValue={entry[0]}>
                                {AntiSignatureFeatures.map((feature, id) =>
                                    <option value={feature} key={id}>{feature}</option>
                                )}
                            </select>
                            <input disabled={requestType !== RequestType.ADD_ANTISIGNATURE} type="text" defaultValue={entry[1]}/>
                            <button disabled={requestType !== RequestType.ADD_ANTISIGNATURE} onClick={(e) => {
                                e.stopPropagation();
                                removeFeature(e.target)
                            }}>-
                            </button>
                        </div>
                    }
                ) : <></>}
                <button disabled={requestType !== RequestType.ADD_ANTISIGNATURE} id="add-feature-button" onClick={addNewFeature}>+
                </button>
                <button disabled={requestType !== RequestType.ADD_ANTISIGNATURE} onClick={() => setShowJsonInput(true)} style={{marginTop: '10px'}}>Import AggregatedBy from JSON
                </button>
            </div>
        </div>
        <div className="antisignature-form">
            <div className="antisignature-form-field">
                <label htmlFor="region">Region</label>
                {requestType === RequestType.ADD_ANTISIGNATURE ? (
                    <div className="region-checkboxes">
                        {Object.values(Region).map((regionValue, idx) => (
                            <label key={idx} className="region-checkbox">
                                <input
                                    type="checkbox"
                                    checked={selectedRegions.has(regionValue)}
                                    onChange={(e) => {
                                        const newSelectedRegions = new Set(selectedRegions);
                                        if (e.target.checked) {
                                            newSelectedRegions.add(regionValue);
                                        } else {
                                            newSelectedRegions.delete(regionValue);
                                        }
                                        setSelectedRegions(newSelectedRegions);
                                    }}
                                />
                                {regionValue}
                            </label>
                        ))}
                    </div>
                ) : (
                    <select disabled={requestType === RequestType.REMOVE_ANTISIGNATURE || requestType === RequestType.UPDATE_ANTISIGNATURE} id="region" defaultValue={antiSignature?.region || Region.NA}
                            autoComplete={Region.NA}
                            onChange={(e) => setRegion(e.target.value as Region)}>
                        {Object.values(Region).map((region, idx) =>
                            <option value={region} key={idx}>{region}</option>
                        )}
                    </select>
                )}
            </div>
            <div className="antisignature-form-field">
                <label htmlFor="signatureType">Signature Type</label>
                <select disabled={requestType === RequestType.REMOVE_ANTISIGNATURE} id="signatureType" defaultValue={antiSignature?.signatureType}
                        onChange={(e) => setSignatureType(e.target.value as AntiSignatureType)}>
                    {Object.values(AntiSignatureType).map((type, id) =>
                        <option value={type} key={id}>{type}</option>
                    )}
                </select>
            </div>
            <div className="antisignature-form-field">
                <label htmlFor="description">Description</label>
                <input disabled={requestType === RequestType.REMOVE_ANTISIGNATURE} onChange={(e) => setDescription(e.target.value)} id="description" type="text"
                       defaultValue={antiSignature?.description}/>
            </div>
            <div className="antisignature-form-field">
                <label htmlFor="models">Models</label>
                <input disabled={requestType === RequestType.REMOVE_ANTISIGNATURE}
                       onChange={(e) => setModels(new Set(e.target.value.split(",").filter(model => model.length > 0)))} id="models" type="text"
                       defaultValue={antiSignature && antiSignature.models ? Array.from(antiSignature.models).join(",") : ""}/>
            </div>
            <div className="antisignature-form-field">
                <label htmlFor="ttl">TTL (days)</label>
                <input disabled={requestType === RequestType.REMOVE_ANTISIGNATURE} onChange={(e) => setTtl(Number.parseInt(e.target.value))} id="ttl"
                       type="number" defaultValue={antiSignature?.ttl || 0}/>
            </div>
            <div className="antisignature-form-field">
                <label htmlFor="labels">Labels</label>
                <input disabled={requestType === RequestType.REMOVE_ANTISIGNATURE}
                       onChange={(e) => setLabels(new Set(e.target.value.split(",").filter(label => label.length > 0)))} id="labels" type="text"
                       defaultValue={antiSignature && antiSignature.labels ? Array.from(antiSignature.labels).join(",") : ""}/>
            </div>
            <div className="antisignature-form-field">
                <label htmlFor="confirm-button">Confirm?</label>
                <button id="confirm-button" onClick={submitRequest}>Yes</button>
                <button onClick={() => setShowWindowForm(false)}>No</button>
            </div>
            <Snackbar
                open={showMessage}
                autoHideDuration={5000}
                onClose={() => setShowMessage(false)}
                message={responseMessage}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
            />
        </div>
        {showJsonInput && (
            <div className="json-input-modal">
                <div className="json-input-content">
                    <h6>Paste JSON to Autocomplete AggregatedBy</h6>
                    <textarea
                        value={jsonInput}
                        onChange={(e) => setJsonInput(e.target.value)}
                        placeholder='Paste your JSON here, e.g. {"aggregatedBy":{"acceptlanguage":"en-US"},"detectionTimestamps":[...]}'
                        style={{ width: '500px', height: '200px'}}
                    />
                    <div className="json-input-buttons">
                        <button onClick={(e) => { e.stopPropagation(); validateJsonInput(); }}>OK</button>
                        <button onClick={(e) => { e.stopPropagation(); setShowJsonInput(false); }}>Cancel</button>
                    </div>
                    {jsonValidationMessage && (
                        <div className={`validation-message ${jsonValidationMessage.includes('Valid') ? 'success' : 'error'}`}>
                            {jsonValidationMessage}
                        </div>
                    )}
                </div>
            </div>
        )}
        <div hidden={requestType === RequestType.REMOVE_ANTISIGNATURE} className="note">Set ttl to 0, if you don't want the antisignature to expire</div>
        <div hidden={requestType === RequestType.REMOVE_ANTISIGNATURE} className="note">Set ttl to -1, if you want to make it expired</div>
    </>
}